import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { useUser } from "@clerk/clerk-react";
import { AdminRoute } from "./components/admin/AdminRoute.tsx";
import { PartnerRoute } from "./components/admin/PartnerRoute.tsx";
import { ProtectedRoute } from "./components/ProtectedRoute.tsx";
import CookieBanner from "./components/CookieBanner.tsx";
import InstallPrompt from "./components/InstallPrompt.tsx";
import ServiceWorkerUpdate from "./components/ServiceWorkerUpdate.tsx";
import { useClerkSupabaseSync } from "./hooks/useClerkSupabaseSync";
import { bootAnalytics, identifyUser } from "./lib/analytics";

// Eager: only the landing (it's the first paint).
import Index from "./pages/Index.tsx";

// Lazy: everything else — code-split into per-page chunks so the
// initial bundle stays small. Lighthouse perf target ≥90.
const SignInPage              = lazy(() => import("./pages/SignIn.tsx"));
const SignUpPage              = lazy(() => import("./pages/SignUp.tsx"));
const SSOCallback             = lazy(() => import("./pages/SSOCallback.tsx"));
const ParentPortal            = lazy(() => import("./pages/ParentPortal.tsx"));
const ChildSettings           = lazy(() => import("./pages/ChildSettings.tsx"));
const Insights                = lazy(() => import("./pages/Insights.tsx"));
const TradePage               = lazy(() => import("./pages/TradePage.tsx"));
const NotFound                = lazy(() => import("./pages/NotFound.tsx"));
const ProPlayground           = lazy(() => import("./pages/ProPlayground.tsx"));
const Onboarding              = lazy(() => import("./pages/Onboarding.tsx"));
const JoinFamily              = lazy(() => import("./pages/JoinFamily.tsx"));
const Subscribe               = lazy(() => import("./pages/Subscribe.tsx"));
// /child/:childId now uses Playground.tsx (Ollie's upstream pattern) — the
// standalone ChildPlayground page was deprecated and our copy was an empty
// 0-byte file blocking junior-miner click-through from /hub.
const Playground              = lazy(() => import("./pages/Playground.tsx"));
const Privacy                 = lazy(() => import("./pages/Privacy.tsx"));
const Terms                   = lazy(() => import("./pages/Terms.tsx"));
const AccountSettings         = lazy(() => import("./pages/AccountSettings.tsx"));
const Pricing                 = lazy(() => import("./pages/Pricing.tsx"));
const AccountSubscription     = lazy(() => import("./pages/AccountSubscription.tsx"));
const AccountAnalytics        = lazy(() => import("./pages/AccountAnalytics.tsx"));
const RewardRequests          = lazy(() => import("./pages/RewardRequests.tsx"));
const MiningCrew              = lazy(() => import("./pages/MiningCrew.tsx"));
const Invite                  = lazy(() => import("./pages/Invite.tsx"));
const DataPage                = lazy(() => import("./pages/Data.tsx"));
const VerifyIdentity          = lazy(() => import("./pages/VerifyIdentity.tsx"));
const Hub                     = lazy(() => import("./pages/Hub.tsx"));

// Admin chunks — separate group, only loaded when an admin actually visits /admin
const AdminLogin              = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminLayout             = lazy(() => import("./pages/admin/AdminLayout.tsx"));
const AdminDashboard          = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminItems              = lazy(() => import("./pages/admin/AdminItems.tsx"));
const AdminCategories         = lazy(() => import("./pages/admin/AdminCategories.tsx"));
const AdminProgression        = lazy(() => import("./pages/admin/AdminProgression.tsx"));
const AdminPromotions         = lazy(() => import("./pages/admin/AdminPromotions.tsx"));
const AdminMarketplaceInsights = lazy(() => import("./pages/admin/AdminMarketplaceInsights.tsx"));
const AdminCustomGoalTrends   = lazy(() => import("./pages/admin/AdminCustomGoalTrends.tsx"));
const PartnerLogin            = lazy(() => import("./pages/admin/PartnerLogin.tsx"));
const AdminSettings           = lazy(() => import("./pages/admin/AdminSettings.tsx"));
const UserManagement          = lazy(() => import("./pages/admin/UserManagement.tsx"));
const AppLibrary              = lazy(() => import("./pages/admin/AppLibrary.tsx"));
const InvestorView            = lazy(() => import("./pages/admin/InvestorView.tsx"));
const CustomerServiceLogs     = lazy(() => import("./pages/admin/CustomerServiceLogs.tsx"));
// Phase B (2026-05-17) — admin coverage for new front-end features
const AdminSubscriptions      = lazy(() => import("./pages/admin/AdminSubscriptions.tsx"));
const AdminKyc                = lazy(() => import("./pages/admin/AdminKyc.tsx"));
const AdminParentalConsents   = lazy(() => import("./pages/admin/AdminParentalConsents.tsx"));
const AdminChildren           = lazy(() => import("./pages/admin/AdminChildren.tsx"));
const AdminReferrals          = lazy(() => import("./pages/admin/AdminReferrals.tsx"));
const AdminMineGuide          = lazy(() => import("./pages/admin/AdminMineGuide.tsx"));
const AdminAuditLog           = lazy(() => import("./pages/admin/AdminAuditLog.tsx"));
const AdminAnalytics          = lazy(() => import("./pages/admin/AdminAnalytics.tsx"));
const AdminPushNotifications  = lazy(() => import("./pages/admin/AdminPushNotifications.tsx"));
const AdminGdpr               = lazy(() => import("./pages/admin/AdminGdpr.tsx"));
const AdminCampaigns          = lazy(() => import("./pages/admin/AdminCampaigns.tsx"));
const AdminEmailTemplates     = lazy(() => import("./pages/admin/AdminEmailTemplates.tsx"));

const queryClient = new QueryClient();

const PLAYGROUND_ENABLED = import.meta.env.VITE_ENABLE_PLAYGROUND === 'true';
const LazyPlayground = PLAYGROUND_ENABLED ? lazy(() => import("./pages/Playground.tsx")) : null;

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0f0f17]">
    <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
  </div>
);

function AppRoutes() {
  // Sync Clerk user to Supabase on every sign-in
  useClerkSupabaseSync();

  // T-C22: boot analytics (gated by cookie consent) + identify user on bridge
  useEffect(() => { bootAnalytics(); }, []);
  const { user } = useUser();
  useEffect(() => {
    const uuid = user?.externalId;
    if (uuid) identifyUser(uuid);
  }, [user?.externalId]);

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path="/parent" element={<ProtectedRoute><ParentPortal /></ProtectedRoute>} />
        <Route path="/parent/:childId" element={<ProtectedRoute><ChildSettings /></ProtectedRoute>} />
        {/* S-EM 2a: /child-original route REMOVED — was unauthenticated bypass */}
        <Route path="/child/:childId" element={<ProtectedRoute><Playground /></ProtectedRoute>} />
        <Route path="/child/:childId/trade" element={<ProtectedRoute><TradePage /></ProtectedRoute>} />
        <Route path="/insights/:childId" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
        <Route path="/pro/:childId" element={<ProtectedRoute><ProPlayground /></ProtectedRoute>} />
        {/* S-EM 2c: /playground gated behind VITE_ENABLE_PLAYGROUND feature flag */}
        <Route path="/playground" element={
          LazyPlayground
            ? <Suspense fallback={null}><LazyPlayground /></Suspense>
            : <Navigate to="/sign-in" replace />
        } />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="items" element={<AdminItems />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="progression" element={<AdminProgression />} />
          <Route path="promotions" element={<AdminPromotions />} />
          <Route path="insights" element={<AdminMarketplaceInsights />} />
          <Route path="custom-goals" element={<AdminCustomGoalTrends />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="apps" element={<AppLibrary />} />
          <Route path="logs" element={<CustomerServiceLogs />} />
          {/* Phase B 2026-05-17 — coverage for new front-end features */}
          <Route path="subscriptions" element={<AdminSubscriptions />} />
          <Route path="kyc" element={<AdminKyc />} />
          <Route path="parental-consents" element={<AdminParentalConsents />} />
          <Route path="children" element={<AdminChildren />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="mine-guide" element={<AdminMineGuide />} />
          <Route path="audit" element={<AdminAuditLog />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="push" element={<AdminPushNotifications />} />
          <Route path="gdpr" element={<AdminGdpr />} />
          <Route path="campaigns" element={<AdminCampaigns />} />
          <Route path="email-templates" element={<AdminEmailTemplates />} />
        </Route>
        {/* /admin/partner-login was a legacy second-gate that asked for a
            second password before app settings. Now redirects into the
            main admin login — settings live inside /admin/settings under
            the regular AdminLayout (sidebar + header), no separate page. */}
        <Route path="/admin/partner-login" element={<Navigate to="/admin/login" replace />} />
        <Route path="/admin/investors" element={<AdminRoute><InvestorView /></AdminRoute>} />
        {/* Settings used to require a second password (PartnerRoute legacy
            from when Ollie had partner-but-not-admin access). Now that
            anyone signed in via /admin/login is a full admin
            (publicMetadata.role='admin'), the extra gate is redundant — and
            LJ flagged that the dual password split was confusing. */}
        <Route path="/admin/settings/*" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/join" element={<JoinFamily />} />
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/data" element={<DataPage />} />
        <Route path="/verify-identity" element={<ProtectedRoute><VerifyIdentity /></ProtectedRoute>} />
        {/* /hub = Ollie's "EARN TO PLAY" profile picker (his Index.tsx, ported verbatim). */}
        <Route path="/hub" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
        <Route path="/settings/*" element={<Navigate to="/admin/settings" replace />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/account/subscription" element={<ProtectedRoute><AccountSubscription /></ProtectedRoute>} />
        <Route path="/account/analytics" element={<ProtectedRoute><AccountAnalytics /></ProtectedRoute>} />
        <Route path="/parent/rewards" element={<ProtectedRoute><RewardRequests /></ProtectedRoute>} />
        <Route path="/parent/crew" element={<ProtectedRoute><MiningCrew /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieBanner />
        <InstallPrompt />
        <ServiceWorkerUpdate />
        <AppRoutes />
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
